
import { useEffect, useState } from "react";
import {
  adminPendingLeavesApi,
  adminApproveLeaveApi,
  adminRejectLeaveApi,
} from "../../app/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  CheckCircle2,
  XCircle,
  Clock,
  User,
  ArrowRight,
  MessageSquare,
  AlertCircle,
  Inbox,
  ShieldAlert,
  Send,
  X
} from "lucide-react";
 
const LEAVE_TYPES = {
  1: "Casual Leave",
  4: "Half Day",
  6: "Short Leave",
  5: "Work From Home",
  9: "Leave Without Pay [LWP]",
};
 
const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};
 
export default function PendingLeaves() {
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectModal, setRejectModal] = useState({
    show: false,
    leaveId: null,
    remarks: "",
  });
 
  useEffect(() => {
    loadPendingLeaves();
  }, []);
 
  const loadPendingLeaves = async () => {
    const res = await adminPendingLeavesApi();
    if (res.success) {
      setLeaves(res.data || []);
    } else {
      toast.error("Failed to load pending leaves");
    }
  };
 
  const approveLeave = async (id) => {
    if (isProcessing) return;
    setIsProcessing(true);
    const res = await adminApproveLeaveApi(id);
    if (res.success) {
      toast.success("Leave approved");
      window.location.reload(); 
      loadPendingLeaves();
    } else {
      toast.error(res.message || "Failed to approve leave");
    }
    setIsProcessing(false);
  };
 
  const openRejectModal = (id) => {
    setRejectModal({ show: true, leaveId: id, remarks: "" });
  };
 
  const handleReject = async () => {
    if (!rejectModal.remarks.trim()) {
      return toast.error("Please enter remarks");
    }
 
    setIsProcessing(true);
    const res = await adminRejectLeaveApi(
      rejectModal.leaveId,
      rejectModal.remarks
    );
 
    if (res.success) {
      toast.success("Leave rejected");
      window.location.reload(); 
      setRejectModal({ show: false, leaveId: null, remarks: "" });
    
      loadPendingLeaves();
    } else {
      toast.error(res.message || "Failed to reject leave");
    }
    setIsProcessing(false);
  };
 
  return (
    <div className="max-w-8xl mx-auto md:mt-20 md:mb-20 ">

    <div className="
            relative overflow-hidden
            rounded-[32px] p-8 md:p-10
            bg-[#12082F]
            backdrop-blur-2xl text-white
            border border-white/10
            shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)]
          ">

      {/* HEADER SECTION */}
      <div className="flex items-center gap-4 mb-10">
        <div className="p-3.5 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-600 shadow-lg shadow-orange-500/20">
          <ShieldAlert className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-semibold  text-white flex items-center gap-4">
            Pending Requests
            {leaves.length > 0 && (
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white ring-4 ring-red-500/10">
                {leaves.length}
              </span>
            )}
          </h1>
          <p className="text-white/50 text-sm font-medium">Review and take action on employee time-off applications</p>
        </div>
      </div>
 
      {/* EMPTY STATE */}
      {leaves.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-24 rounded-[48px] bg-white/5 border border-white/10 border-dashed">
          <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center mb-6">
            <Inbox className="w-10 h-10 text-white/10" />
          </div>
          <p className="text-white/40 font-black uppercase tracking-[3px] text-sm">All caught up!</p>
          <p className="text-white/20 text-xs mt-1">No pending leave requests at the moment.</p>
        </div>
      ) : (
        <>




{/* <thead className="text-white/70 border-b border-white/10">
            <tr>
              <th className="p-3 text-left">Employee</th>
              <th className="p-3 text-left">Leave Type</th>
              <th className="p-3 text-left">From</th>
              <th className="p-3 text-left">To</th>
              <th className="p-3">Days</th>
              <th className="p-3 text-left">Comment</th>
              <th className="p-3">Action</th>
            </tr>
          </thead> */}


          {/* DESKTOP TABLE */}
          <div className="hidden lg:block overflow-hidden rounded-lg bg-white/5 border border-white/10 backdrop-blur-2xl">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className=" p-3 text-left text-sm text-white/60">Employee</th>
                  <th className="p-3 text-left text-sm text-white/60">Category</th>
                  <th className="p-3 text-left text-sm text-white/60">Schedule</th>
                  <th className="p-3 text-left text-sm text-white/60">Duration</th>
                  <th className="p-3 text-left text-sm text-white/60">Reason</th>
                  <th className="p-3 text-left text-sm text-white/60 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {leaves.map((leave) => (
                  <tr key={leave.id} className="group hover:bg-white/[0.04] transition-all">
                    <td className="p-3">
                      <button
                        onClick={() => navigate(`/users/${leave.employee_id}`)}
                        className="flex items-center gap-3 group/user"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg group-hover/user:scale-110 transition duration-300">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm  text-white group-hover/user:text-blue-400">{leave.first_name} {leave.last_name}</p>
                          {/* <p className="text-[10px] font-bold text-white/30 uppercase tracking-tighter">ID #{leave.employee_id}</p> */}
                        </div>
                      </button>
                    </td>
                    <td className="p-3">
                      <span className="px-3 py-1.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-300 text-[10px] font-semibold uppercase tracking-widest">
                        {LEAVE_TYPES[leave.leave_type_id]}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 text-white/80">
                        <span className="text-sm ">{formatDate(leave.leave_start_date)}</span>
                        <ArrowRight className="w-3 h-3 text-white/20" />
                        <span className="text-sm">{formatDate(leave.leave_end_date)}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-baseline gap-1 text-white">
                        <span className="text-lg font-semibold">{leave.duration}</span>
                        <span className="text-[10px] font-semibold text-white/40 uppercase">Days</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <p className="text-xs text-white/60 italic max-w-[200px] truncate" title={leave.employee_comment}>
                        {leave.employee_comment || "-"}
                      </p>
                    </td>
                    <td className="p-3">
                      {/* <div className="flex  justify-center gap-2 opacity-100 group-hover:scale-105 transition-all">
                        <button
                          onClick={() => approveLeave(leave.id)}
                          disabled={isProcessing}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-500/20 border border-green-500/20 text-green-400 hover:bg-green-500 hover:text-white transition-all text-[90px] text-[10px] font-black uppercase tracking-widest"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Approve
                        </button>
                        <button
                          onClick={() => openRejectModal(leave.id)}
                          disabled={isProcessing}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-all text-[10px] font-black uppercase tracking-widest"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Reject
                        </button>
                      </div> */}





<div className="flex justify-center gap-3 group-hover:scale-105 transition-all">

  <button
    onClick={() => approveLeave(leave.id)}
    disabled={isProcessing}
    className="flex items-center gap-1.5 px-3 py-0 rounded-lg
    bg-green-500/10 border border-green-500/30
    text-green-400 hover:bg-green-500 hover:text-white
    transition-all text-xs font-semibold uppercase tracking-wide"
  >
    <CheckCircle2 className="w-4 h-4" />
    Approve
  </button>

  <button
    onClick={() => openRejectModal(leave.id)}
    disabled={isProcessing}
    className="flex items-center gap-1.5 px-3 py-2 rounded-lg
    bg-red-500/10 border border-red-500/30
    text-red-400 hover:bg-red-500 hover:text-white
    transition-all text-xs font-semibold uppercase tracking-wide"
  >
    <XCircle className="w-4 h-4" />
    Reject
  </button>

</div>





                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
 
          {/* MOBILE LIST */}
          <div className="lg:hidden space-y-4">
            {leaves.map((leave) => (
              <div
                key={leave.id}
                className="bg-white/5 border border-white/10 rounded-[32px] p-6 backdrop-blur-xl shadow-2xl overflow-hidden"
              >
                <div className="flex flex-col justify-between items-start mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-500 flex items-center justify-center shadow-lg">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white whitespace-nowrap">{leave.first_name} {leave.last_name}</h3>
                      <p className="text-[10px] uppercase font-bold text-white/30">PENDING APPROVAL</p>
                    </div>
                  </div>
                  <div className="px-3 py-1.5 mt-2 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-black uppercase">
                    {LEAVE_TYPES[leave.leave_type_id]}
                  </div>
                </div>
 
                <div className="grid grid-cols-2 gap-4 mb-6 p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                  <div className="space-y-1">
                    <span className="text-[9px] font-black text-white/30 uppercase">From</span>
                    <p className="text-xs font-bold text-white/80">{formatDate(leave.leave_start_date)}</p>
                  </div>
                  <div className="space-y-1 text-right">
                    <span className="text-[9px] font-black text-white/30 uppercase">To</span>
                    <p className="text-xs font-bold text-white/80">{formatDate(leave.leave_end_date)}</p>
                  </div>
                  <div className="col-span-2 pt-3 border-t border-white/5 flex justify-between items-center">
                    <span className="text-[10px] font-black text-white/40 uppercase">Total Duration</span>
                    <span className="text-lg font-black text-white">{leave.duration} Days</span>
                  </div>
                </div>
 
                {leave.employee_comment && (
                  <div className="flex gap-3 items-start mb-8 px-2">
                    <MessageSquare className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-xs italic text-white/50 leading-relaxed">"{leave.employee_comment}"</p>
                  </div>
                )}
 
                <div className="flex gap-3">
                  <button
                    onClick={() => approveLeave(leave.id)}
                    className="flex-1 py-3.5 rounded-2xl bg-green-500 font-semibold text-black text-sm uppercase tracking-[2px] transition hover:bg-green-400"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => openRejectModal(leave.id)}
                    className="flex-1 py-3.5 rounded-2xl bg-green-500 font-semibold text-black text-sm uppercase tracking-[2px] transition hover:bg-green-400"
                  >
                    Rejectx
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
 
      {/* REJECT MODAL */}
      {rejectModal.show &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={() => setRejectModal({ show: false, leaveId: null, remarks: "" })} />
            <div className="
              relative w-full max-w-lg overflow-hidden
              rounded-[48px] p-10
              bg-[#12082F]
              border border-white/20 shadow-2xl animate-scaleIn
              text-white
            ">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-2xl bg-red-500/20 shadow-lg shadow-red-500/10">
                    <ShieldAlert className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black tracking-tight">Decline Request</h2>
                    <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Reason for rejection</p>
                  </div>
                </div>
                <button onClick={() => setRejectModal({ show: false, leaveId: null, remarks: "" })} className="p-2 rounded-full hover:bg-white/5 transition">
                  <X className="w-6 h-6 text-white/20 hover:text-white" />
                </button>
              </div>
 
              <div className="relative group">
                <div className="absolute left-5 top-5 text-white/20 group-focus-within:text-red-400 transition">
                  <AlertCircle className="w-5 h-5" />
                </div>
                <textarea
                  placeholder="State the reason why this application is being declined..."
                  value={rejectModal.remarks}
                  onChange={(e) => setRejectModal({ ...rejectModal, remarks: e.target.value })}
                  className="w-full min-h-[160px] p-6 pl-14 rounded-[32px] bg-white/5 border border-white/10 focus:outline-none focus:border-red-500/50 focus:bg-white/[0.08] transition text-white placeholder-white/30 text-sm italic leading-relaxed"
                />
              </div>
 
              <div className="flex justify-end gap-4 mt-10">
                <button
                  onClick={() => setRejectModal({ show: false, leaveId: null, remarks: "" })}
                  className="px-6 py-4 rounded-2xl bg-white/5 hover:bg-white/10 transition font-bold text-xs uppercase tracking-widest"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={isProcessing}
                  className="flex items-center gap-3 px-10 py-4 rounded-2xl bg-gradient-to-r from-red-600 to-pink-700 hover:from-red-500 hover:to-pink-600 transition shadow-xl shadow-red-900/40 font-black text-xs uppercase tracking-widest"
                >
                  <Send className="w-4 h-4" />
                  Decline Request
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
    </div>
  );
}


// import { useEffect, useState } from "react";
// import {
//   adminPendingLeavesApi,
//   adminApproveLeaveApi,
//   adminRejectLeaveApi,
// } from "../../app/api";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";
// import { createPortal } from "react-dom";
// const LEAVE_TYPES = {
//   1: "Casual Leave",
//   4: "Half Day",
//   6: "Short Leave",
//   5: "Work From Home",
// };

// const formatDate = (date) =>
//   new Date(date).toLocaleDateString("en-GB");

// export default function PendingLeaves() {
//   const navigate = useNavigate();
//   const [leaves, setLeaves] = useState([]);
//   const [rejectModal, setRejectModal] = useState({
//     show: false,
//     leaveId: null,
//     remarks: "",
//   });

//   useEffect(() => {
//     loadPendingLeaves();
//   }, []);

//   const loadPendingLeaves = async () => {
//     const res = await adminPendingLeavesApi();
//     if (res.success) {
//       setLeaves(res.data || []);
//     } else {
//       toast.error("Failed to load pending leaves");
//     }
//   };

//   const approveLeave = async (id) => {
//     const res = await adminApproveLeaveApi(id);
//     if (res.success) {
//       toast.success("Leave approved");
//       window.location.reload();   // reload page

//       loadPendingLeaves();
//     } else {
//       toast.error(res.message || "Failed to approve leave");
//     }
//   };

//   const openRejectModal = (id) => {
//     setRejectModal({ show: true, leaveId: id, remarks: "" });
//   };

//   const handleReject = async () => {
//     if (!rejectModal.remarks.trim()) {
//       return toast.error("Please enter remarks");
//     }

//     const res = await adminRejectLeaveApi(
//       rejectModal.leaveId,
//       rejectModal.remarks
//     );

//     if (res.success) {
//       toast.success("Leave rejected");
//       window.location.reload();   // reload page

//       setRejectModal({ show: false, leaveId: null, remarks: "" });
//       loadPendingLeaves();
//     } else {
//       toast.error(res.message || "Failed to reject leave");
//     }
//   };

//   return (
//     <div className="rounded-2xl p-6 md:mt-20 bg-gradient-to-br from-[#2d1c6a] via-[#24155a] to-[#12082f] text-white shadow-2xl">

//       <h2 className="font-bold text-2xl mb-6">
//         Pending Leave Requests
//       </h2>

//       {/* ================= DESKTOP TABLE ================= */}
//       <div className="hidden md:block overflow-x-auto">
//         <table className="min-w-[900px] w-full text-sm">
          // <thead className="text-white/70 border-b border-white/10">
          //   <tr>
          //     <th className="p-3 text-left">Employee</th>
          //     <th className="p-3 text-left">Leave Type</th>
          //     <th className="p-3 text-left">From</th>
          //     <th className="p-3 text-left">To</th>
          //     <th className="p-3">Days</th>
          //     <th className="p-3 text-left">Comment</th>
          //     <th className="p-3">Action</th>
          //   </tr>
          // </thead>

//           <tbody>
//             {leaves.length === 0 ? (
//               <tr>
//                 <td colSpan="7" className="py-8 text-center opacity-60">
//                   No pending leaves found
//                 </td>
//               </tr>
//             ) : (
//               leaves.map((leave) => (
//                 <tr key={leave.id} className="border-b border-white/5 hover:bg-white/5">
//                   <td className="p-3 font-medium">
//                     <button
                   
                      
//                       onClick={() => navigate(`/users/${leave.employee_id}`)}

                      
//                       className="text-blue-300 hover:underline"
//                     >
//                       {leave.first_name} {leave.last_name}
                 

//                     </button>
//                   </td>

//                   <td className="p-3">
//                     {LEAVE_TYPES[leave.leave_type_id]}
//                   </td>

//                   <td className="p-3">
//                     {formatDate(leave.leave_start_date)}
//                   </td>

//                   <td className="p-3">
//                     {formatDate(leave.leave_end_date)}
//                   </td>

//                   <td className="p-3 text-center">
//                     {leave.duration}
//                   </td>

//                   <td className="p-3 text-white/70 max-w-[220px] truncate">
//                     {leave.employee_comment || "-"}
//                   </td>

//                   <td className="p-3">
//                     <div className="flex gap-2">
//                       <button
//                         onClick={() => approveLeave(leave.id)}
//                         className="px-3 py-1 rounded-full bg-green-500/20 text-green-300 text-xs"
//                       >
//                         Approve
//                       </button>

//                       <button
//                         onClick={() => openRejectModal(leave.id)}
//                         className="px-3 py-1 rounded-full bg-red-500/20 text-red-300 text-xs"
//                       >
//                         Reject
//                       </button>
//                     </div>
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* ================= MOBILE CARDS ================= */}
//       <div className="md:hidden space-y-3">
//         {leaves.map((leave) => (
//           <div
//             key={leave.id}
//             className="bg-gradient-to-br from-[#2d1c6a] to-[#12082f] border border-white/10 rounded-2xl p-4 shadow-lg"
//           >
//             <div className="flex justify-between mb-2">
//               <p
//                 className="font-semibold text-blue-300 cursor-pointer"
           
                
//                 onClick={() => navigate(`/users/${leave.employee_id}`)}

//               >
//                 {leave.first_name} {leave.last_name}
//               </p>
//               <span className="text-xs bg-white/10 px-2 py-1 rounded">
//                 {LEAVE_TYPES[leave.leave_type_id]}
//               </span>
//             </div>

//             <div className="grid grid-cols-2 gap-3 text-sm">
//               <div>
//                 <p className="text-white/60 text-xs">From</p>
//                 <p>{formatDate(leave.leave_start_date)}</p>
//               </div>

//               <div>
//                 <p className="text-white/60 text-xs">To</p>
//                 <p>{formatDate(leave.leave_end_date)}</p>
//               </div>

//               <div>
//                 <p className="text-white/60 text-xs">Days</p>
//                 <p className="font-semibold text-blue-300">
//                   {leave.duration}
//                 </p>
//               </div>

//               <div>
//                 <p className="text-white/60 text-xs">Reason</p>
//                 <p className="truncate">
//                   {leave.employee_comment || "-"}
//                 </p>
//               </div>
//             </div>

//             <div className="flex gap-3 mt-4">
//               <button
//                 onClick={() => approveLeave(leave.id)}
//                 className="flex-1 py-2 rounded-lg bg-green-500/20 text-green-300 text-sm"
//               >
//                 Approve
//               </button>

//               <button
//                 onClick={() => openRejectModal(leave.id)}
//                 className="flex-1 py-2 rounded-lg bg-red-500/20 text-red-300 text-sm"
//               >
//                 Reject
//               </button>
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* REJECT MODAL (unchanged) */}
  

// {rejectModal.show &&
//   createPortal(
//     <div className="fixed inset-0 z-[99999] flex items-center justify-center">

//       {/* Overlay */}
//       <div
//         className="absolute inset-0 bg-black/70 backdrop-blur-md"
//         onClick={() =>
//           setRejectModal({
//             show: false,
//             leaveId: null,
//             remarks: "",
//           })
//         }
//       />

//       {/* Glass Modal */}
//       <div
//         className="relative w-full max-w-lg mx-4 p-8 rounded-2xl
//                    bg-white/10 backdrop-blur-xl
//                    border border-white/20
//                    shadow-[0_0_40px_rgba(0,0,0,0.4)]
//                    text-white"
//       >
//         <h2 className="text-2xl font-semibold mb-6">
//           Reject Leave
//         </h2>

//         <textarea
//           placeholder="Enter remarks..."
//           value={rejectModal.remarks}
//           onChange={(e) =>
//             setRejectModal({
//               ...rejectModal,
//               remarks: e.target.value,
//             })
//           }
//           className="w-full p-4 rounded-xl bg-white/10
//                      border border-white/20
//                      focus:outline-none
//                      focus:ring-2 focus:ring-purple-400
//                      min-h-[120px]
//                      text-white placeholder-white/60"
//         />

//         <div className="flex justify-end gap-4 mt-6">
//           <button
//             onClick={() =>
//               setRejectModal({
//                 show: false,
//                 leaveId: null,
//                 remarks: "",
//               })
//             }
//             className="px-5 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition"
//           >
//             Cancel
//           </button>

//           <button
//             onClick={handleReject}
//             className="px-5 py-2 rounded-lg bg-red-500 hover:bg-red-600 transition"
//           >
//             Reject
//           </button>
//         </div>
//       </div>
//     </div>,
//     document.body
//   )}


//     </div>
//   );
// }

